import React, { useState, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { API_URLS } from '../config';
import './styles/SurveyQuestionsSettings.css';

const SurveyQuestionsSettings = ({ businessId, questions, setQuestions, onSettingsChange }) => {
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [visibleQuestions, setVisibleQuestions] = useState([]);
  const [previousVisibleQuestions, setPreviousVisibleQuestions] = useState([]);
  const [isSurveyEnabled, setIsSurveyEnabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const maxQuestions = 7;

  // Updated useEffect for better transition management
  useEffect(() => {
    // Store previous state to help with animations
    setPreviousVisibleQuestions(visibleQuestions);

    if (!questions || !Array.isArray(questions)) return;
    const newVisibleQuestions = [];

    // First 4 questions are always visible (0-3)
    for (let i = 0; i < Math.min(4, maxQuestions); i++) {
      newVisibleQuestions.push(i);
    }
    
    // For questions 5-7 (indices 4-6)
    for (let i = 4; i < maxQuestions; i++) {
      // Show this question if ANY of these conditions are true:
      // 1. This question has data
      // 2. The previous question has data
      // 3. Any later question has data
      
      if (i < questions.length && hasContent(questions[i])) {
        newVisibleQuestions.push(i);
        continue;
      }
      
      if (i > 0 && i - 1 < questions.length && hasContent(questions[i-1])) {
        newVisibleQuestions.push(i);
        continue;
      }
      
      // Check if any later questions have content
      let showDueToLaterContent = false;
      for (let j = i + 1; j < maxQuestions && j < questions.length; j++) {
        if (hasContent(questions[j])) {
          showDueToLaterContent = true;
          break;
        }
      }
      
      if (showDueToLaterContent) {
        newVisibleQuestions.push(i);
      }
    }
    
    setVisibleQuestions(newVisibleQuestions);
  }, [questions]);

  const hasContent = (q) => {
    return q && (q.text || q.topic);
  };

  const handleQuestionChange = (id, field, value) => {
    // Check if this is a new question (id starts with "new-")
    const isNewQuestion = id.toString().startsWith('new-');
    
    if (isNewQuestion) {
      const index = parseInt(id.split('-')[1], 10);
      
      setQuestions(prevQuestions => {
        const newQuestions = [...prevQuestions];
        // Create a new question object if it doesn't exist at this index
        if (index >= newQuestions.length) {
          // Fill any gaps in the array
          for (let i = newQuestions.length; i <= index; i++) {
            newQuestions[i] = { id: `temp-${i}`, topic: '', text: '', topicId: null };
          }
        }
        // Update the field
        newQuestions[index] = { 
          ...newQuestions[index],
          [field]: value 
        };
        return newQuestions;
      });
    } else {
      // Handle existing questions as before
      setQuestions(prevQuestions =>
        prevQuestions.map(q =>
          q.id === id ? { ...q, [field]: value } : q
        )
      );
    }
    // Clear messages on change
    setError('');
    setSuccessMessage('');
  };

  const isQuestionDisabled = (questions, index) => {
    if (index < 3) {
      return false; // First 3 questions are always enabled
    }

    if (index === 3) {
      return !hasContent(questions[2]); // Question 4 depends on question 3
    }

    // For questions 5-7:
    if (hasContent(questions[index])) {
      return false;
    }

    return !hasContent(questions[index - 1]);
  };

  const getTransitionItems = () => {
    const items = [];

    for (let i = 0; i < maxQuestions; i++) {
      if (visibleQuestions.includes(i)) {
        // If index exists in questions array, use it, otherwise create a new empty question object
        const question = i < questions.length ? questions[i] : { id: `new-${i}`, topic: '', text: '', topicId: null };
        items.push({
          id: `question-${i}`,
          index: i,
          question: question,
          disabled: isQuestionDisabled(questions, i),
        });
      }
    }

    return items;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSubmitting(true);
    
    const token = localStorage.getItem('token');
    if (!token || !businessId) {
      setError("Authentication token or Business ID is missing.");
      setIsSubmitting(false);
      return;
    }
    if (!questions || questions.length === 0) {
      setError("No questions to save.");
      setIsSubmitting(false);
      return;
    }

    // Prepare payload using the questions from props
    const payload = questions.map(q => ({
      question_id: q.id,
      question_text: q.text,
      topic_id: q.topicId,
      topic_name: q.topic,
      enabled: isSurveyEnabled
    }));

    try {
      const response = await fetch(API_URLS.questions(businessId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage('Survey questions updated successfully!');
        if (onSettingsChange) {
          onSettingsChange();
        }
      } else {
        console.error('Failed to update survey questions:', data.message);
        setError(data.message || 'Failed to update survey questions');
      }
    } catch (error) {
      console.error('Error updating survey questions:', error);
      setError('An error occurred while updating survey questions.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filledCount = questions ? questions.filter(q => q.text || q.topic).length : 0;

  return (
    <form onSubmit={handleSubmit}>
      <h3>Survey Questions</h3>

      {error && <p className="error-message">{error}</p>}
      {successMessage && <p className="success-message">{successMessage}</p>}
      
      {(!questions || questions.length === 0) && !error && (
        <p>No survey questions found or loaded.</p>
      )}

      <TransitionGroup className="survey-questions-container">
        {getTransitionItems().map((item) => (
          <CSSTransition
            key={item.id}
            timeout={300}
            classNames="question-item"
            appear
          >
            <div className={`question-bubble ${item.disabled ? 'disabled' : ''}`}>
              <input
                type="text"
                className="topic-field"
                value={item.question.topic || ''} 
                onChange={(e) => handleQuestionChange(item.question.id, 'topic', e.target.value)}
                placeholder={`Topic ${item.index + 1}`}
                disabled={item.disabled || !isSurveyEnabled}
              />
              <input
                type="text"
                className="question-field"
                value={item.question.text || ''}
                onChange={(e) => handleQuestionChange(item.question.id, 'text', e.target.value)}
                placeholder="Question"
                disabled={item.disabled || !isSurveyEnabled}
              />
            </div>
          </CSSTransition>
        ))}
      </TransitionGroup>

      <p className="questions-counter">
        {filledCount} of {maxQuestions} questions added
      </p>

      <button 
        type="submit" 
        className="save-button"
        disabled={isSubmitting || !isSurveyEnabled}
      >
        {isSubmitting ? 'Saving...' : 'Save Changes'}
      </button>
    </form>
  );
};

export default SurveyQuestionsSettings;